import { useQueryClient } from '@zg-rentals/react-query5';

import { Button, ButtonGroup, Heading, ModalDialog, Paragraph, TextButton } from '@zillow/constellation';
import type {
  MaintenanceTaskStatusDto,
  RentalManagerUserMaintenanceTaskCountDto,
  RentalManagerUserMaintenanceTaskDto,
  RentalManagerUserMaintenanceTaskListDto,
} from '@zg-rentals/rental-manager-env';

import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { useMaintenanceTrackerTaskUserWebApiHandler_delete } from '@zg-rentals/ts-maintenance-tracker-client-react-query';

import {
  emitDeleteTaskFromTaskDetailPageEvent,
  emitDeleteTaskFromTaskTableEvent,
} from '../clickstream/DeleteTaskEvent';
import { TASK_DETAILS_PAGE, TASKS_TABLE, USE_TASKS_LIST } from '../constants/TaskConstants';

type Props = {
  maintenanceTaskId: string;
  onClose: (deleted?: boolean) => void;
  fromExternalApp?: boolean;
  listingAlias?: string;
  clickOriginationPoint?: string;
};

type MaintenanceTask = {
  maintenanceTaskId: string;
  status: MaintenanceTaskStatusDto;
  count: RentalManagerUserMaintenanceTaskCountDto;
};

type MaintenanceTaskSummary = {
  maintenanceTasks: Array<MaintenanceTask>;
};

export const DeleteTaskModal = ({
  maintenanceTaskId,
  onClose,
  fromExternalApp,
  listingAlias,
  clickOriginationPoint,
}: Props) => {
  const queryClient = useQueryClient();
  const { user } = useRentalManagerUser();

  const { mutate: deleteTask } = useMaintenanceTrackerTaskUserWebApiHandler_delete();

  return (
    <ModalDialog
      size={{ default: 'fullScreen', md: 'sm' }}
      isOpen={true}
      onClose={() => onClose(false)}
      renderHeader={<Heading level={1}>Delete this task?</Heading>}
      renderBody={<Paragraph>You won’t be able to view or restore this task if you delete it.</Paragraph>}
      renderFooter={
        <ButtonGroup aria-label="Modal actions">
          <TextButton onClick={() => onClose(false)}>Cancel</TextButton>
          <Button
            buttonType="primary"
            onClick={() => {
              const queryKey = fromExternalApp ? ['getMaintenanceTasks', listingAlias] : [USE_TASKS_LIST];

              deleteTask(
                { getParams: { maintenanceTaskId } },
                {
                  onSuccess: () => {
                    if (clickOriginationPoint === TASKS_TABLE) {
                      emitDeleteTaskFromTaskTableEvent({ user });
                    } else if (clickOriginationPoint === TASK_DETAILS_PAGE) {
                      emitDeleteTaskFromTaskDetailPageEvent({ user });
                    }

                    let oldData: Array<MaintenanceTask> | Array<RentalManagerUserMaintenanceTaskDto> | undefined;

                    if (fromExternalApp) {
                      oldData = queryClient.getQueryData<MaintenanceTaskSummary>(queryKey)?.maintenanceTasks;
                    } else {
                      oldData = queryClient.getQueryData<RentalManagerUserMaintenanceTaskListDto>(queryKey)?.tasks;
                    }

                    if (!oldData) {
                      return;
                    }
                    const indexOfTaskToDelete = oldData.findIndex(
                      (item) => item.maintenanceTaskId === maintenanceTaskId,
                    );
                    oldData.splice(indexOfTaskToDelete, 1)[0];

                    queryClient.setQueryData(queryKey, {
                      [fromExternalApp ? 'maintenanceTasks' : 'tasks']: oldData,
                    });
                  },
                  onSettled: () => {
                    queryClient.refetchQueries({ queryKey });
                    onClose(true);
                  },
                },
              );
            }}
          >
            Yes, delete
          </Button>
        </ButtonGroup>
      }
    />
  );
};
